import { type ComponentProps, type ReactNode, useState } from 'react';
import clsx from 'clsx';

import useSlider from '../../hooks/useSlider';

import useAutoScroll from './useAutoScroll';

import styles from './Carousel.module.css';

const DEFAULT_INTERVAL = 5000;
const PAGE_SIZE = 4;

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.71967 12.5303C7.42678 12.2374 7.42678 11.7626 7.71967 11.4697L15.2197 3.96967C15.5126 3.67678 15.9874 3.67678 16.2803 3.96967C16.5732 4.26256 16.5732 4.73744 16.2803 5.03033L9.31066 12L16.2803 18.9697C16.5732 19.2626 16.5732 19.7374 16.2803 20.0303C15.9874 20.3232 15.5126 20.3232 15.2197 20.0303L7.71967 12.5303Z"
      fill="currentColor"
    />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    className={className}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.2803 11.4697C16.5732 11.7626 16.5732 12.2374 16.2803 12.5303L8.78033 20.0303C8.48744 20.3232 8.01256 20.3232 7.71967 20.0303C7.42678 19.7374 7.42678 19.2626 7.71967 18.9697L14.6893 12L7.71967 5.03033C7.42678 4.73744 7.42678 4.26256 7.71967 3.96967C8.01256 3.67678 8.48744 3.67678 8.78033 3.96967L16.2803 11.4697Z"
      fill="currentColor"
    />
  </svg>
);

function chunk(items: ReactNode[], size: number) {
  const pages: ReactNode[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages;
}

interface CarouselProps extends ComponentProps<'div'> {
  items: ReactNode[];
  autoScroll?: boolean;
  infiniteLoop?: boolean;
  interval?: number;
}

export default function Carousel({
  items,
  autoScroll = true,
  infiniteLoop = true,
  interval = DEFAULT_INTERVAL,
  className,
  ...props
}: CarouselProps) {
  const pages = chunk(items, PAGE_SIZE);
  const shouldLoop = infiniteLoop && pages.length > 1;
  const carouselPages = shouldLoop ? [pages.at(-1)!, ...pages, pages[0]] : pages;

  const { scrollerRef, next, prev, canScroll, isAtStart, isAtEnd } = useSlider({
    itemCount: pages.length,
    infiniteLoop: shouldLoop,
  });

  const [isPaused, setIsPaused] = useState(false);

  useAutoScroll({
    enabled: autoScroll && canScroll && !isPaused && (shouldLoop || !isAtEnd),
    advance: next,
    interval,
  });

  return (
    <div
      {...props}
      className={clsx(styles.wrapper, className)}
      role="region"
      aria-roledescription="carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      {canScroll && (
        <button
          type="button"
          className={clsx(styles.arrow, styles.arrowPrev)}
          onClick={prev}
          aria-label="Previous"
          disabled={!shouldLoop && isAtStart}
        >
          <ChevronLeftIcon className={styles.arrowIcon} />
        </button>
      )}

      <div
        ref={scrollerRef}
        className={styles.scroller}
      >
        {carouselPages.map((page, pageIndex) => (
          <div
            key={pageIndex}
            className={styles.page}
            aria-hidden={shouldLoop && (pageIndex === 0 || pageIndex === carouselPages.length - 1)}
            inert={shouldLoop && (pageIndex === 0 || pageIndex === carouselPages.length - 1) ? true : undefined}
          >
            {page.map((item, index) => (
              <div
                key={index}
                className={styles.tile}
              >
                {item}
              </div>
            ))}
          </div>
        ))}
      </div>

      {canScroll && (
        <button
          type="button"
          className={clsx(styles.arrow, styles.arrowNext)}
          onClick={next}
          aria-label="Next"
          disabled={!shouldLoop && isAtEnd}
        >
          <ChevronRightIcon className={styles.arrowIcon} />
        </button>
      )}
    </div>
  );
}
